import { Box, Tooltip, Typography } from '@mui/material';
import { memo, useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { Calendar, dayjsLocalizer } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import dayjs from 'dayjs';
import 'dayjs/locale/uk';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';

dayjs.locale('uk');
const localizer = dayjsLocalizer(dayjs);
const DragAndDropCalendar = withDragAndDrop(Calendar);
const MIN_TIME = new Date(1970, 0, 1, 10, 0, 0);
const MAX_TIME = new Date(1970, 0, 1, 22, 0, 0);
const SCROLL_TO = MIN_TIME;

const statusColors = {
  SCHEDULED: '#E0E7FF',
  COMPLETED: '#DCFCE7',
  RESCHEDULED: '#FEF3C7',
  CANCELLED: '#FEE2E2',
};

const calendarFormats = {
  timeGutterFormat: (date) => dayjs(date).format('HH:mm'),
  eventTimeRangeFormat: ({ start, end }) =>
    `${dayjs(start).format('HH:mm')} – ${dayjs(end).format('HH:mm')}`,
  dayFormat: (date) => dayjs(date).format('dd DD MMM'),
  weekdayFormat: (date) => dayjs(date).format('dd'),
};

const calendarMessages = {
  today: 'Сьогодні',
  previous: 'Назад',
  next: 'Вперед',
  week: 'Тиждень',
  showMore: (count) => `+${count} ще`,
};

const EventContent = ({
  event,
  tooltipOpen,
  onHoverStart,
  onHoverEnd,
  onInteractionStart,
  showAuditInfo,
}) => {
  const tooltipTitle = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {event.lessonDeleted && (
        <Typography variant="caption" sx={{ color: '#DC2626', fontWeight: 600 }}>
          ⚠ Заняття видалено — уточніть статус
        </Typography>
      )}
      {event.unconfirmed && !event.lessonDeleted && (
        <Typography variant="caption" sx={{ color: '#FF521B', fontWeight: 600 }}>
          ⚠ Призначте точний час заняття
        </Typography>
      )}
      <Typography variant="subtitle2" fontWeight={600}>
        {event.studentName || event.title}
      </Typography>
      <Typography variant="caption">
        {dayjs(event.start).format('HH:mm')} – {dayjs(event.end).format('HH:mm')}
      </Typography>
      {event.comment && <Typography variant="caption">{event.comment}</Typography>}
      {showAuditInfo && event.createdAt && (
        <Typography variant="caption" sx={{ color: '#B0B8C9', mt: 0.5 }}>
          Додано: {dayjs(event.createdAt).format('DD.MM.YYYY HH:mm')}
        </Typography>
      )}
      {showAuditInfo && event.updatedAt && (
        <Typography variant="caption" sx={{ color: '#B0B8C9' }}>
          Змінено: {dayjs(event.updatedAt).format('DD.MM.YYYY HH:mm')}
        </Typography>
      )}
    </Box>
  );

  return (
    <Tooltip
      title={tooltipTitle}
      arrow
      placement="top"
      disableHoverListener
      disableFocusListener
      disableTouchListener
      open={tooltipOpen}
      onClose={onHoverEnd}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onMouseEnter={onHoverStart}
        onMouseLeave={onHoverEnd}
        onMouseDown={onInteractionStart}
        onMouseUp={onHoverEnd}
        onTouchStart={(e) => {
          onInteractionStart();
          onHoverStart(e);
        }}
        onTouchEnd={onHoverEnd}
      >
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            lineHeight: 1.2,
            textAlign: 'center',
          }}
        >
          {event.studentName || event.title}
        </Typography>
        {(event.unconfirmed || event.lessonDeleted) && (
          <Box
            sx={{
              width: 18,
              height: 18,
              borderRadius: '50%',
              border: `1.5px solid ${event.lessonDeleted ? '#EF4444' : '#FF521B'}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              ml: 0.5,
            }}
          >
            <Typography sx={{ fontSize: 11, fontWeight: 800, lineHeight: 1, color: event.lessonDeleted ? '#EF4444' : '#FF521B' }}>
              !
            </Typography>
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};

const WeeklyCalendar = ({ weekStart, events = [], onCellClick, onEventClick, onEventMove, showAuditInfo = false }) => {
  const isSelectable = Boolean(onCellClick);
  const isDraggable = Boolean(onEventMove);
  const [tooltipEventId, setTooltipEventId] = useState(null);
  const tooltipTimerRef = useRef(null);
  const [pendingEvents, setPendingEvents] = useState({});

  const clearTooltipTimer = useCallback(() => {
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
      tooltipTimerRef.current = null;
    }
  }, []);

  const handleHoverStart = useCallback(
    (eventId) => {
      clearTooltipTimer();
      tooltipTimerRef.current = setTimeout(() => {
        setTooltipEventId(eventId);
      }, 1000);
    },
    [clearTooltipTimer]
  );

  const handleHoverEnd = useCallback(() => {
    clearTooltipTimer();
    setTooltipEventId(null);
  }, [clearTooltipTimer]);

  useEffect(
    () => () => {
      clearTooltipTimer();
    },
    [clearTooltipTimer]
  );

  const calendarDate = useMemo(() => dayjs(weekStart).toDate(), [weekStart]);

  useEffect(() => {
    setPendingEvents((prev) => {
      if (!Object.keys(prev).length) return prev;
      const next = { ...prev };
      (events || []).forEach((event) => {
        const pending = next[event.id];
        if (!pending) return;
        const startMatches = dayjs(event.startTime).isSame(pending.start, 'minute');
        const endMatches = dayjs(event.endTime).isSame(pending.end, 'minute');
        if (startMatches && endMatches) {
          delete next[event.id];
        }
      });
      return next;
    });
  }, [events]);

  const normalizedEvents = useMemo(() => {
    return (events || []).map((event) => {
      const pending = pendingEvents[event.id];
      const startDate = pending ? pending.start : dayjs(event.startTime);
      const endDate = pending ? pending.end : dayjs(event.endTime);
      return {
        ...event,
        start: startDate.toDate(),
        end: endDate.toDate(),
        title: event.studentName || 'Заняття',
      };
    });
  }, [events, pendingEvents]);

  const handleSelectSlot = useCallback(
    ({ start }) => {
      if (!onCellClick) return;
      onCellClick(dayjs(start).second(0).millisecond(0));
    },
    [onCellClick]
  );

  const handleSelectEvent = useCallback(
    (event) => {
      onEventClick?.(event);
    },
    [onEventClick]
  );

  const handleEventDrop = useCallback(
    ({ event, start, end }) => {
      if (!isDraggable) return;
      const newStart = dayjs(start);
      const newEnd = dayjs(end);
      handleHoverEnd();
      setPendingEvents((prev) => ({
        ...prev,
        [event.id]: { start: newStart, end: newEnd },
      }));
      onEventMove?.(event, newStart, newEnd);
    },
    [handleHoverEnd, isDraggable, onEventMove]
  );

  const handleEventResize = useCallback(
    ({ event, start, end }) => {
      if (!isDraggable) return;
      const newStart = dayjs(start);
      const newEnd = dayjs(end);
      handleHoverEnd();
      setPendingEvents((prev) => ({
        ...prev,
        [event.id]: { start: newStart, end: newEnd },
      }));
      onEventMove?.(event, newStart, newEnd);
    },
    [handleHoverEnd, isDraggable, onEventMove]
  );

  const eventPropGetter = useCallback((event) => {
    const backgroundColor = event.lessonDeleted ? '#F1F5F9' : event.unconfirmed ? '#E2E8F0' : (statusColors[event.status] || '#E2E8F0');
    return {
      title: '',
      style: {
        backgroundColor,
        borderRadius: 12,
        border: '1px solid rgba(15,23,42,0.1)',
        padding: '10px 14px',
        color: '#0f172a',
        boxShadow: '0px 10px 18px rgba(15,23,42,0.12)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        margin: 0,
      },
    };
  }, []);

  const dayPropGetter = useCallback((date) => {
    const isWeekend = [0, 6].includes(dayjs(date).day());
    return {
      style: {
        backgroundColor: isWeekend ? '#F8FAFC' : '#fff',
      },
    };
  }, []);

  return (
    <Box
      sx={{
        border: '1px solid #E2E8F0',
        borderRadius: 2,
        bgcolor: '#fff',
        overflowX: 'auto',
        minWidth: 0,
        '& .rbc-time-view': { minWidth: 700 },
        '& .rbc-time-header': {
          backgroundColor: '#F8FAFC',
        },
        '& .rbc-time-header-cell .rbc-header': {
          padding: '12px 0',
          borderBottom: 'none',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        },
        '& .rbc-header a': {
          textAlign: 'center',
          display: 'block',
          width: '100%',
        },
        '& .rbc-allday-cell': {
          display: 'none',
        },
        '& .rbc-time-content': {
          borderTop: '1px solid #E2E8F0',
        },
        '& .rbc-time-view': {
          minHeight: 780,
        },
        '& .rbc-timeslot-group': {
          borderBottom: '1px solid #F1F5F9',
        },
        '& .rbc-time-gutter .rbc-timeslot-group': {
          alignItems: 'center',
          justifyContent: 'center',
        },
        '& .rbc-time-gutter .rbc-time-slot': {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          fontWeight: 500,
        },
        '& .rbc-slot-label': {
          textAlign: 'center',
        },
        '& .rbc-event-label': {
          display: 'none',
        },
        '& .rbc-day-slot .rbc-events-container': {
          margin: '0 !important',
        },
        '& .rbc-day-slot .rbc-event, & .rbc-day-slot .rbc-background-event': {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
        '& .rbc-event-content': {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          width: '100%',
        },
      }}
    >
      <DndProvider backend={HTML5Backend}>
        <DragAndDropCalendar
          localizer={localizer}
          events={normalizedEvents}
          date={calendarDate}
          view="week"
          toolbar={false}
          selectable={isSelectable ? 'ignoreEvents' : false}
          step={60}
          timeslots={1}
          min={MIN_TIME}
          max={MAX_TIME}
          scrollToTime={SCROLL_TO}
          formats={calendarFormats}
          messages={calendarMessages}
          components={{
            event: (componentProps) => (
              <EventContent
                {...componentProps}
                tooltipOpen={tooltipEventId === componentProps.event.id}
                onHoverStart={() => handleHoverStart(componentProps.event.id)}
                onHoverEnd={handleHoverEnd}
                onInteractionStart={handleHoverEnd}
                showAuditInfo={showAuditInfo}
              />
            ),
          }}
          popup={false}
          resizable={false}
          onSelectSlot={isSelectable ? handleSelectSlot : undefined}
          onSelectEvent={handleSelectEvent}
          onEventDrop={isDraggable ? handleEventDrop : undefined}
          onEventResize={isDraggable ? handleEventResize : undefined}
          tooltipAccessor={() => ''}
          eventPropGetter={eventPropGetter}
          dayPropGetter={dayPropGetter}
          onNavigate={() => {}}
          draggableAccessor={() => isDraggable}
          style={{ height: 780 }}
          longPressThreshold={300}
        />
      </DndProvider>
    </Box>
  );
};

export default memo(WeeklyCalendar);

